import React, { useState } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  LayoutDashboard,
  Building2,
  Users,
  DollarSign,
  FileText,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  LogOut,
  UserCog // Added UserCog icon for the Users page
} from "lucide-react";
import { NotificationToast, useNotifications } from "@/components/shared/NotificationToast";
import { DeleteWithUndoProvider } from "@/components/shared/DeleteWithUndo";
import { useAuth } from "@/context/AuthContext";
import { canAccessPage } from "@/lib/permissions"; // Added permission checker

const navigationItems = [
  { title: "Dashboard", url: createPageUrl("Dashboard"), icon: LayoutDashboard, pageName: "Dashboard" },
  { title: "Apartments", url: createPageUrl("Apartments"), icon: Building2, pageName: "Apartments" },
  { title: "Clients", url: createPageUrl("Clients"), icon: Users, pageName: "Clients" },
  { title: "Contracts", url: createPageUrl("Contracts"), icon: FileText, pageName: "Contracts" },
  { title: "Sales & Payments", url: createPageUrl("SalesAndPayments"), icon: DollarSign, pageName: "SalesAndPayments" },
  { title: "Reports", url: createPageUrl("Reports"), icon: FileText, pageName: "Reports" },
  { title: "Users Management", url: createPageUrl("Users"), icon: UserCog, pageName: "Users" }, // Added Users page
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const { notifications, addNotification, removeNotification } = useNotifications();
  const { logout, user } = useAuth(); // Added 'user' to check roles

  // Filter navigation items based on the logged-in user's role
  const visibleNavItems = navigationItems.filter(item => 
    canAccessPage(user?.role, item.pageName)
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  React.useEffect(() => {
    window.addNotification = addNotification;
  }, [addNotification]);

  return (
    <>
      <style>{`:root { --primary-navy: #1e3a5f; --primary-gold: #d4af37; --accent-teal: #2dd4bf; --bg-cream: #fafaf9; --text-dark: #1f2937; } .custom-scrollbar::-webkit-scrollbar { width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }`}</style>
      <div className="h-screen flex w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
        
        {/* Desktop Sidebar */}
        <aside className={`hidden md:flex md:flex-col border-r border-slate-200 bg-white/80 backdrop-blur-sm h-screen flex-shrink-0 transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? 'md:w-20' : 'md:w-64'
        }`}>
          {/* Header / Logo */}
          <div className={`border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 flex-shrink-0 flex items-center ${
            isSidebarCollapsed ? 'p-4 justify-center' : 'p-6 gap-3'
          }`}>
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div className="overflow-hidden">
                <h2 className="font-bold text-white text-lg leading-tight">Alrwid</h2>
                <p className="text-xs text-slate-300 truncate">Construction & Contracting</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 overflow-y-auto custom-scrollbar">
            <div className="space-y-1">
              {!isSidebarCollapsed && (
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-2">
                  Management
                </p>
              )}
              {/* Changed navigationItems to visibleNavItems */}
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.url;
                return (
                  <Link
                    key={item.title}
                    to={item.url}
                    className={`flex items-center ${isSidebarCollapsed ? 'justify-center px-2' : 'px-4 gap-3'} py-3 rounded-xl mb-1 transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-slate-900 to-slate-800 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                    title={isSidebarCollapsed ? item.title : undefined}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-amber-400' : ''}`} />
                    {!isSidebarCollapsed && (
                      <span className="font-medium whitespace-nowrap">{item.title}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Logout Button (Desktop) */}
          <div className="flex-shrink-0 p-3 border-t border-slate-200">
            <button
              onClick={handleLogout}
              className={`flex items-center w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'px-4 gap-3'} py-3 rounded-xl transition-all duration-200 text-red-600 hover:bg-red-50`}
              title={isSidebarCollapsed ? "Logout" : undefined}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {!isSidebarCollapsed && (
                <span className="font-medium whitespace-nowrap">Logout</span>
              )}
            </button>
          </div>

          {/* Collapse Toggle Button */}
          <div className="flex-shrink-0 p-4 border-t border-slate-200 flex justify-center">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
              title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isSidebarCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside
          className={`fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-300 md:hidden ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="border-b border-slate-200 p-6 bg-gradient-to-r from-slate-900 to-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-white text-lg">Alrwid</h2>
                <p className="text-xs text-slate-300">Construction & Contracting</p>
              </div>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="text-white hover:bg-slate-700 p-1 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex-1 p-3 overflow-y-auto flex flex-col">
            <div className="space-y-1 flex-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-2">
                Management
              </p>
              {/* Changed navigationItems to visibleNavItems */}
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.url;
                return (
                  <Link
                    key={item.title}
                    to={item.url}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-slate-900 to-slate-800 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-amber-400' : ''}`} />
                    <span className="font-medium">{item.title}</span>
                  </Link>
                );
              })}
            </div>
            
            {/* Logout Button (Mobile) */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <button
                onClick={() => {
                  handleLogout();
                  setIsSidebarOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all duration-200 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
          {/* Mobile Header */}
          <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 md:hidden flex-shrink-0">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="hover:bg-slate-100 p-2 rounded-lg transition-colors duration-200"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-lg font-bold text-slate-900">Alrwid</h1>
              </div>
            </div>
          </header>

          {/* Scrollable Page Content */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-8">
            <DeleteWithUndoProvider>
              <Outlet />
            </DeleteWithUndoProvider>
          </div>
        </main>
        
        <NotificationToast notifications={notifications} onRemove={removeNotification} />
      </div>
    </>
  );
}